import React, { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { Customer } from '../../../types';
import ConfirmDialog from '../../../shared/components/ConfirmDialog';
import { useCustomerKB } from '../hooks/useCustomerKB';
import CustomerKBContent from './CustomerKBContent';

interface CustomerKBModalProps {
  isOpen: boolean;
  customer: Customer | null;
  onClose: () => void;
}

const CustomerKBModal: React.FC<CustomerKBModalProps> = ({
  isOpen,
  customer,
  onClose,
}) => {
  const kb = useCustomerKB({ isOpen, customer });

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '80vh', maxHeight: '80vh' } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon color="primary" />
            <Typography variant="h6">Knowledge Base - {customer?.name}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <CustomerKBContent
          knowledgeBaseId={customer?.knowledge_base_id}
          knowledgeBaseBucket={customer?.knowledge_base_bucket}
          isKBConfigured={!!kb.isKBConfigured}
          documents={kb.documents}
          totalSize={kb.totalSize}
          loading={kb.loading}
          uploading={kb.uploading}
          syncing={kb.syncing}
          error={kb.error}
          successMessage={kb.successMessage}
          currentSyncJob={kb.currentSyncJob}
          onUpload={kb.handleFileUpload}
          onDownload={kb.handleDownload}
          onDelete={kb.handleDelete}
          onSync={kb.handleSync}
          onClearError={kb.clearError}
          onClearSuccess={kb.clearSuccess}
          formatBytes={kb.formatBytes}
          formatDate={kb.formatDate}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>

      <ConfirmDialog
        open={kb.docToDelete !== null}
        title="Delete Document"
        onCancel={() => kb.setDocToDelete(null)}
        onConfirm={kb.executeDelete}
        confirmText="Delete"
        confirmColor="error"
      >
        Delete &quot;{kb.docToDelete?.filename}&quot;? This cannot be undone.
      </ConfirmDialog>
    </Dialog>
  );
};

export default memo(CustomerKBModal);
